/**
 * Squarespace-style color palette presets + variant generators.
 * Used by DashboardStyles and the Onboarding palette step.
 */

export const PALETTE_GROUPS = [
  {
    group: 'Vibrant',
    palettes: [
      { id: 'fuchsia-violet', label: 'Fuchsia / Violet', swatches: ['#FAFAFA', '#E5DDFF', '#A78BFA', '#8B5CF6', '#0E0E23'] },
      { id: 'midnight', label: 'Midnight Blue', swatches: ['#F8FAFC', '#CBD5E1', '#3B82F6', '#1E3A8A', '#0F172A'] },
      { id: 'sunset', label: 'Sunset', swatches: ['#FFF7ED', '#FDBA74', '#F97316', '#9A3412', '#1E1B4B'] },
      { id: 'rose', label: 'Rose Garden', swatches: ['#FFF1F2', '#FECDD3', '#FB7185', '#9F1239', '#0F172A'] },
      { id: 'electric', label: 'Electric', swatches: ['#FAFAFA', '#FDE68A', '#FACC15', '#F59E0B', '#0A0A0A'] },
    ],
  },
  {
    group: 'Earth Tones',
    palettes: [
      { id: 'sage', label: 'Sage & Stone', swatches: ['#F4F1EA', '#D6CFC2', '#96A78D', '#5A6B4E', '#2E2F2A'] },
      { id: 'terracotta', label: 'Terracotta', swatches: ['#FBF6F1', '#E8C9B0', '#C97B5A', '#8B4A33', '#2A1810'] },
      { id: 'desert', label: 'Desert Tan', swatches: ['#FAF3E6', '#E8D4A8', '#C69963', '#7A5230', '#231811'] },
      { id: 'olive', label: 'Olive Grove', swatches: ['#F6F4ED', '#D9D5B8', '#9C9667', '#5B5A2E', '#1E1D14'] },
      { id: 'clay', label: 'Clay & Cream', swatches: ['#FAF0E0', '#E5C8A4', '#B5734F', '#7A3B1F', '#2D160C'] },
      { id: 'moss', label: 'Forest Moss', swatches: ['#F0F4ED', '#C7D3B8', '#788B66', '#3F5132', '#181F12'] },
    ],
  },
  {
    group: 'Coastal',
    palettes: [
      { id: 'oceanic', label: 'Oceanic', swatches: ['#F0F9FF', '#BAE6FD', '#0EA5E9', '#075985', '#0C1929'] },
      { id: 'seaglass', label: 'Sea Glass', swatches: ['#F0FDFA', '#A7F3D0', '#14B8A6', '#0F766E', '#0A1F1D'] },
      { id: 'driftwood', label: 'Driftwood', swatches: ['#F8F5F0', '#D6CDBE', '#8A7E68', '#4A4233', '#1A1610'] },
      { id: 'tropic', label: 'Tropic', swatches: ['#FFFEEC', '#FEF08A', '#34D399', '#047857', '#052E2B'] },
    ],
  },
  {
    group: 'Pastel',
    palettes: [
      { id: 'cotton-candy', label: 'Cotton Candy', swatches: ['#FFFFFF', '#FCE7F3', '#F9A8D4', '#A78BFA', '#1F1A2E'] },
      { id: 'pistachio', label: 'Pistachio', swatches: ['#FAFBF7', '#DDE5C5', '#9DBE8B', '#6B8E68', '#1E2A1C'] },
      { id: 'lavender', label: 'Soft Lavender', swatches: ['#FBFAFF', '#E0DBFF', '#A89BE0', '#6B5BAA', '#1B1632'] },
      { id: 'peach', label: 'Sweet Peach', swatches: ['#FFF8F1', '#FFD9B8', '#FFA577', '#C8633A', '#2A1308'] },
    ],
  },
  {
    group: 'Neon',
    palettes: [
      { id: 'cyberpunk', label: 'Cyberpunk', swatches: ['#0A0A0F', '#1F1F2E', '#FF006E', '#00F5FF', '#FAFAFA'] },
      { id: 'matrix', label: 'Matrix', swatches: ['#000000', '#0D0D0D', '#00FF88', '#39FF14', '#F0F0F0'] },
      { id: 'vapor', label: 'Vaporwave', swatches: ['#1A0033', '#FF71CE', '#01CDFE', '#B967FF', '#FFFB96'] },
      { id: 'arcade', label: 'Arcade', swatches: ['#0A0A1F', '#FF1493', '#9D00FF', '#00E5FF', '#FFEA00'] },
    ],
  },
  {
    group: 'Monochrome',
    palettes: [
      { id: 'mono-light', label: 'Light Mono', swatches: ['#FFFFFF', '#E5E7EB', '#6B7280', '#1F2937', '#000000'] },
      { id: 'mono-warm', label: 'Warm Mono', swatches: ['#FAF8F5', '#D6D3CE', '#6B675F', '#262522', '#000000'] },
      { id: 'mono-cool', label: 'Cool Mono', swatches: ['#F8FAFC', '#CBD5E1', '#475569', '#0F172A', '#000000'] },
      { id: 'mono-dark', label: 'Dark Mono', swatches: ['#0A0A0A', '#1F1F1F', '#525252', '#A3A3A3', '#FAFAFA'] },
    ],
  },
];

/** Flattened list for legacy code that imported PRESET_PALETTES. */
export const PRESET_PALETTES = PALETTE_GROUPS.flatMap(g => g.palettes);

/** Build a custom palette from a single hue (0-360) — derives 5 swatches via lightness ramps. */
export const buildPaletteFromHue = (hue, sat = 65) => {
  const hsl = (h, s, l) => `hsl(${h} ${s}% ${l}%)`;
  return [hsl(hue, Math.min(sat, 30), 96), hsl(hue, sat, 85), hsl(hue, sat, 60), hsl(hue, sat, 35), hsl(hue, sat, 12)];
};
